import classNames from 'classnames';
import { memo, useCallback, useRef, useEffect, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useParams } from 'react-router-dom';
import { useReactivePropertyManager } from '../../modules/Properties/reactive/useReactivePropertyManager';
import { PropertyType } from '../../modules/Properties/types';
import { calculateNiceNumbers } from '../../utils/niceNumbers';
import { useHeatmapLegendOverlay } from '../../hooks/modules/project/useHeatmapLegendOverlay';
import { GradientType } from '../../modules/Properties/types';
import { Gradients } from '../../potree/materials/Gradients';
import { useT } from '../../translation/src';

const getGradientColors = (gradient: GradientType): string[] => {
  const threeGradient = Gradients[gradient];

  if (!threeGradient) {
    // Fallback to grayscale if gradient not found
    return ['rgb(0, 0, 0)', 'rgb(255, 255, 255)'];
  }

  return threeGradient.map(([_, color]) => `rgb(${Math.round(color.r * 255)}, ${Math.round(color.g * 255)}, ${Math.round(color.b * 255)})`);
};

const drawHeatmapLegend = (ctx: CanvasRenderingContext2D, canvas: HTMLCanvasElement, gradient: GradientType, tolerance: number) => {
  // Constants for layout and styling
  const LAYOUT_CONFIG = {
    CANVAS_PADDING: {
      TOP: 20,
      BOTTOM: 15,
      LEFT: 20,
      RIGHT: 60,
    },
    GRADIENT: {
      WIDTH: 30,
      OFFSET_FROM_TEXT: 4,
    },
    TEXT: {
      FONT: '12px Arial',
      MIN_SPACING: 16,
      MIN_EDGE_DISTANCE: 12,
    },
    TICK: {
      LINE_WIDTH: 1,
      EXTEND_LENGTH: 0,
    },
  } as const;

  const TICK_GENERATION_CONFIG = {
    DECIMAL_PRECISION: tolerance > 0.02 ? 2 : 3, // special case, increase precision for lowest values to show some ticks
  } as const;

  // Types
  type TickValue = {
    value: number;
    position: number;
  };

  const generateTickValues = (tolerance: number): number[] => {
    const { DECIMAL_PRECISION } = TICK_GENERATION_CONFIG;

    // Use the nice numbers system to generate values from 0 to tolerance
    const { values } = calculateNiceNumbers(0, tolerance, 6);

    // Round to decimal precision
    const roundedValues = values.map((val) => Math.round(val * Math.pow(10, DECIMAL_PRECISION)) / Math.pow(10, DECIMAL_PRECISION));

    // Ensure we always include 0 and tolerance as endpoints
    const allValues = [0, ...roundedValues.filter((val) => val > 0 && val < tolerance), tolerance];

    // Remove duplicates and sort
    return [...new Set(allValues)].sort((a, b) => a - b);
  };

  const calculateLayout = (canvasHeight: number) => {
    const { TOP, BOTTOM, LEFT } = LAYOUT_CONFIG.CANVAS_PADDING;
    const { WIDTH, OFFSET_FROM_TEXT } = LAYOUT_CONFIG.GRADIENT;

    const topTextY = TOP;
    const bottomTextY = canvasHeight - BOTTOM;
    const gradientLeft = LEFT;
    const gradientTop = topTextY - OFFSET_FROM_TEXT;
    const gradientHeight = bottomTextY - topTextY;

    return {
      topTextY,
      bottomTextY,
      gradientLeft,
      gradientTop,
      gradientWidth: WIDTH,
      gradientHeight,
    };
  };

  const drawTickMarksAndLabels = (ctx: CanvasRenderingContext2D, validValues: TickValue[], layout: ReturnType<typeof calculateLayout>, tolerance: number, canvas: HTMLCanvasElement) => {
    const { gradientLeft, gradientTop, gradientHeight } = layout;
    const tickEndX = canvas.width - LAYOUT_CONFIG.CANVAS_PADDING.RIGHT;
    const extendedTickEndX = tickEndX + LAYOUT_CONFIG.TICK.EXTEND_LENGTH;

    validValues.forEach(({ value, position }) => {
      const ratio = (tolerance - value) / tolerance;
      const lineY = gradientTop + ratio * gradientHeight;

      // Draw tick line extending from start of gradient to extended end
      ctx.beginPath();
      ctx.moveTo(gradientLeft, lineY);
      ctx.lineTo(extendedTickEndX, lineY);
      ctx.stroke();

      // Draw label at the end of the extended line
      ctx.fillText(value.toFixed(TICK_GENERATION_CONFIG.DECIMAL_PRECISION), extendedTickEndX, position);
    });
  };

  const setupCanvasStyle = (ctx: CanvasRenderingContext2D, canvas: HTMLCanvasElement) => {
    const computedStyle = getComputedStyle(canvas);
    const textColor = computedStyle.color;
    const tickColor = computedStyle.accentColor || computedStyle.outlineColor || textColor; // fallback to text color

    ctx.strokeStyle = tickColor;
    ctx.lineWidth = LAYOUT_CONFIG.TICK.LINE_WIDTH;
    ctx.fillStyle = textColor;
    ctx.font = LAYOUT_CONFIG.TEXT.FONT;
    ctx.textAlign = 'left';
  };

  const createTickValues = (allValues: number[], tolerance: number, topTextY: number, bottomTextY: number): TickValue[] => {
    const { MIN_EDGE_DISTANCE } = LAYOUT_CONFIG.TEXT;

    return allValues
      .map((value) => {
        const ratio = (tolerance - value) / tolerance;
        const position = topTextY + ratio * (bottomTextY - topTextY);
        return { value, position };
      })
      .filter((tickValue, index) => {
        // Always include the bottom value (0)
        if (index === allValues.length - 1) return true;

        // Filter out top values that are too close to the edge
        const tooCloseToTopEdge = tickValue.position < topTextY + MIN_EDGE_DISTANCE;
        return !tooCloseToTopEdge;
      });
  };

  const createCanvasGradient = (ctx: CanvasRenderingContext2D, colors: string[], canvasHeight: number): CanvasGradient => {
    const gradient = ctx.createLinearGradient(0, canvasHeight, 0, 0);
    if (colors.length === 1) {
      // Single color - fill entire gradient with it
      gradient.addColorStop(0, colors[0]);
      gradient.addColorStop(1, colors[0]);
    } else {
      colors.forEach((color, index) => {
        gradient.addColorStop(index / (colors.length - 1), color);
      });
    }
    return gradient;
  };

  ctx.clearRect(0, 0, canvas.width, canvas.height);

  // Calculate layout
  const layout = calculateLayout(canvas.height);

  // Generate tick values
  const allValues = generateTickValues(tolerance);
  const validValues = createTickValues(allValues, tolerance, layout.topTextY, layout.bottomTextY);

  // Create and draw gradient (first layer)
  const colors = getGradientColors(gradient);
  const gradientObj = createCanvasGradient(ctx, colors, canvas.height);

  ctx.fillStyle = gradientObj;
  ctx.fillRect(layout.gradientLeft, layout.gradientTop, layout.gradientWidth, layout.gradientHeight);

  // Setup canvas styling and draw ticks/labels (on top of gradient)
  setupCanvasStyle(ctx, canvas);
  drawTickMarksAndLabels(ctx, validValues, layout, tolerance, canvas);
};

interface HeatmapLegendOverlayProps {
  className?: string;
}

const HeatmapLegendOverlay_: React.FC<HeatmapLegendOverlayProps> = ({ className }) => {
  const { projectId = '' } = useParams();
  const service = useReactivePropertyManager({ projectId });
  const { overlayVisible, planeIdentifier, gradient, tolerance } = useHeatmapLegendOverlay(projectId);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [themeChange, setThemeChange] = useState(0);
  const heatmapLegend = useT('heatmap legend', { swc: true });
  const closeLegendText = useT('close legend', { swc: true });

  const handleClose = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();

      if (planeIdentifier) {
        service.setValue('heatmap_legend', planeIdentifier, PropertyType.PROJECT_USER, false);
      }
    },
    [planeIdentifier, service],
  );

  const handleOverlayClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  // Watch for dark mode changes
  useEffect(() => {
    const observer = new MutationObserver(() => setThemeChange((prev) => prev + 1));
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  // Draw the legend on canvas
  useEffect(() => {
    if (!overlayVisible) return;

    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');

    if (!canvas || !ctx || tolerance <= 0) {
      return;
    }

    drawHeatmapLegend(ctx, canvas, gradient, tolerance);
  }, [overlayVisible, gradient, tolerance, themeChange]);

  if (!overlayVisible) {
    return null;
  }

  return (
    <div
      className={classNames('absolute top-[66px] right-4 z-20', 'bg-[#363636]/90 dark:bg-[#242424]/90', 'border border-gray-300 dark:border-gray-600', 'rounded-lg shadow-lg', 'p-4', 'max-w-xs', className)}
      onClick={handleOverlayClick}
      data-screenshot-overlay="heatmap-legend"
    >
      {/* Header with close button */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-white">{heatmapLegend}</h3>
        <button onClick={handleClose} className="p-1.5 rounded-md text-gray-400 hover:text-gray-800 hover:bg-black/5 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-300 transition-colors" aria-label={closeLegendText}>
          <XMarkIcon className="w-4 h-4" />
        </button>
      </div>

      {/* Legend content */}
      <div className="flex justify-center">
        <canvas ref={canvasRef} width={145} height={200} className="border border-gray-300 accent-gray-400 rounded-md text-white" />
      </div>
    </div>
  );
};

export const HeatmapLegendOverlay = memo(HeatmapLegendOverlay_);
